import type { GeneratedProfileCount } from "../types/StatisticsTypes.ts";

export type StatisticsPreset = "thisMonth" | "lastMonth" | "thisYear";

/**
 * The picked days as the instants the statistics endpoint expects (RDT-435): the start of the first day
 * and the start of the day after the last one, both in the browser's time zone. The stored generation
 * times are UTC, so this keeps month boundaries where the user sees them.
 */
export function toPeriod(firstDay: Date, lastDay: Date): { from: string; to: string } {
    const from = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
    const to = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
}

/** First and last day of a preset period, relative to the browser's "now". */
export function presetDays(preset: StatisticsPreset, now: Date = new Date()): [Date, Date] {
    const year = now.getFullYear();
    const month = now.getMonth();
    switch (preset) {
        case "thisMonth":
            return [new Date(year, month, 1), new Date(year, month + 1, 0)];
        case "lastMonth":
            return [new Date(year, month - 1, 1), new Date(year, month, 0)];
        case "thisYear":
            return [new Date(year, 0, 1), new Date(year, 11, 31)];
    }
}

/** Sum over the given rows, for the table's total line. */
export function sumCounts(rows: GeneratedProfileCount[]): Omit<GeneratedProfileCount, "product"> {
    return rows.reduce(
        (sum, row) => ({ total: sum.total + row.total, real: sum.real + row.real, test: sum.test + row.test }),
        { total: 0, real: 0, test: 0 },
    );
}

const isoDay = (day: Date) =>
    `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;

/** CSV file name carrying the picked period, e.g. `profile-statistics_2026-10-01_2026-10-31`. */
export function exportFileName(firstDay: Date, lastDay: Date): string {
    return `profile-statistics_${isoDay(firstDay)}_${isoDay(lastDay)}`;
}
